// src/data/workKind.js
//
// SECTION 536 (2026-09-30). Bob, on claim 0005 ("Junior worked ahd of me"; "Seniority Article | Scope Article"): "When
// events of this nature, a pop up should appear with this kind of explanation, no?" Yes. A claim that says a junior man
// got the work, or that names an article with no rule number, cannot be matched to a rule until the claimant says WHAT
// WORK it was - overtime or extra work is one rule, a bid position another, vacation another. The claimant is the one
// person who knows, and he is the one filing, so Quick File asks him THEN, in a box on the screen, with the explanation,
// instead of the claim waiting days for someone to ask.
//
// Pure. The rule each kind of work points to is stated ONLY where the agreement is catalogued and the rule was read off
// it (Conrail Mechanical Supervisors, 1994 schedule: Rule 7 TEMPORARY ASSIGNMENTS holds the overtime distribution in
// 7(C) - claim 0006 was moved there; Rule 3 SELECTION OF POSITIONS holds bids in 3(C); Rule 13 VACATIONS - claim 0012
// was moved to 13(D)). For every other agreement the answer is recorded and the Local Chairperson matches the rule;
// nothing is guessed.

import { agreementFamily } from './agreementRegistry';   // ruling 312 phase 1 (d)

export const WORK_KINDS = [
  { key: 'overtime', label: 'Overtime or extra work', hint: 'A shift, a day or hours outside his regular assignment - overtime, a call, an extra day.' },
  { key: 'position', label: 'A position he bid on', hint: 'A job, vacancy or assignment that was bulletined or should have been, and went to the junior man.' },
  { key: 'vacation', label: 'Vacation or time off', hint: 'Vacation weeks or days picked, granted or scheduled ahead of him.' },
  { key: 'other', label: 'Something else', hint: 'Say in a few words what it was.' },
];
export const WORK_KIND_KEYS = WORK_KINDS.map(k => k.key);

// Where the rule for each kind was read off the agreement. Keyed by the agreement name as Quick File carries it.
const RULES_BY_AGREEMENT = [
  {
    family: 'conrail-ms',   // ruling 312 phase 1 (d): src\data\agreementRegistry.js names the agreement
    name: 'Conrail Mechanical Supervisors (1994 schedule)',
    term: 'Rule',
    rules: {
      overtime: { num: '7', title: 'TEMPORARY ASSIGNMENTS', part: '7(C)', why: 'Rule 7(C) is how overtime and extra Foreman work is distributed at the location.' },
      position: { num: '3', title: 'SELECTION OF POSITIONS', part: '3(C)', why: 'Rule 3(C) is how positions are bulletined and awarded by seniority.' },
      vacation: { num: '13', title: 'VACATIONS', part: '13(D)', why: 'Rule 13(D) is how vacation is scheduled by seniority.' },
    },
  },
];
export function agreementRules(agreement) { const f = agreementFamily(agreement); return (f && RULES_BY_AGREEMENT.find(a => a.family === f)) || null; }

// A rule label with no number in it names no rule ("Seniority Article", "Scope Article").
export function unnumbered(matchedRules) {
  return (Array.isArray(matchedRules) ? matchedRules : []).filter(r => r && r.rule && !/\d/.test(String(r.rule)));
}
// "junior worked ahead of me", "younger man got it before me", "less senior ... over me" - spelling as members write it.
export function juniorAhead(text) {
  const t = String(text || '').toLowerCase();
  return /\b(junior|jr\.?|younger|less senior|lower senior\w*)\b/.test(t) && /\b(ahead|ahd|ahed|before|over|instead)\b/.test(t);
}
// Ask when the claim cannot be matched to a rule without knowing the work, and it has not been answered.
export function needsWorkKind({ matchedRules, description, workKind } = {}) {
  if (workKind && WORK_KIND_KEYS.concat(['unsaid']).includes(workKind)) return false;
  const numbered = (Array.isArray(matchedRules) ? matchedRules : []).filter(r => r && /\d/.test(String(r.rule || '')));
  if (unnumbered(matchedRules).length > 0) return true;
  return juniorAhead(description) && numbered.length === 0;
}

// What the box says for this agreement: each kind, with the rule it points to where known.
export function workKindChoices(agreement) {
  const a = agreementRules(agreement);
  return WORK_KINDS.map(k => {
    const r = a && a.rules[k.key];
    return { ...k, rule: r ? a.term + ' ' + r.part : null, why: r ? r.why : null };
  });
}
export function explanation(agreement, matchedRules) {
  const a = agreementRules(agreement);
  const names = unnumbered(matchedRules).map(r => r.rule);
  const head = names.length
    ? 'Your claim names ' + names.map(n => '"' + n + '"').join(' and ') + ' but no rule number.'
    : 'Your claim says someone junior to you got the work.';
  const tail = a
    ? ' Which rule applies depends on what the work was - under the ' + a.name + ' it is a different rule for overtime, for a bid position and for vacation. Tell us which, and the rule is filled in for you.'
    : ' Which rule applies depends on what the work was. Tell us which; your Local Chairperson matches it to the rule in your agreement.';
  return head + tail;
}

// The answer applied: the unnumbered labels are replaced by the rule the kind points to (when known), a rule already
// chosen is kept, nothing is duplicated. Returns the new matchedRules and the fields recorded on the claim.
export function applyWorkKind({ agreement, matchedRules, kind, note } = {}) {
  const k = WORK_KIND_KEYS.includes(kind) ? kind : 'unsaid';
  const list = Array.isArray(matchedRules) ? matchedRules.filter(Boolean) : [];
  const a = agreementRules(agreement);
  const r = a && a.rules[k];
  let rules = list;
  if (r) {
    const label = a.term + ' ' + r.num + ' — ' + r.title;
    const numOf = (x) => (String(x.rule).match(/\d+/) || [])[0] || '';
    rules = list.filter(x => /\d/.test(String(x.rule)));
    if (!rules.some(x => numOf(x) === r.num)) rules = rules.concat([{ rule: label, relevance: r.why }]);
  }
  const n = String(note || '').trim().slice(0, 300);
  return { matchedRules: rules, fields: { workKind: k, workKindNote: (k === 'other' || k === 'unsaid') ? (n || null) : null } };
}
